'use client';

import { useSyncExternalStore } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import Link from 'next/link';
import { Button } from '@/components/ui/button';
import { MainNav } from '@/components/main-nav';
import { NotificationSystem } from '@/components/notifications/notification-system';
import { LogOut, User } from 'lucide-react';
import { toast } from 'sonner';

const AUTH_STATUS_EVENT = 'auth-status-changed';

function readAuthStatus() {
  if (typeof document === 'undefined') return false;
  const authStatus = document.cookie
    .split('; ')
    .find(row => row.startsWith('auth-status='));
  return authStatus?.split('=')[1] === 'authenticated';
}

function subscribeToAuthStatus(onStoreChange: () => void) {
  window.addEventListener(AUTH_STATUS_EVENT, onStoreChange);
  window.addEventListener('storage', onStoreChange);
  return () => {
    window.removeEventListener(AUTH_STATUS_EVENT, onStoreChange);
    window.removeEventListener('storage', onStoreChange);
  };
}

export function AuthHeader() {
  const router = useRouter();
  usePathname();
  const isAuthenticated = useSyncExternalStore(
    subscribeToAuthStatus,
    readAuthStatus,
    () => false
  );

  const handleLogout = async () => {
    try {
      // Call logout API to clear server-side cookies
      await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include', // Important: include cookies
      });
      
      // Clear client-side cookies as backup
      document.cookie = 'auth-token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
      document.cookie = 'auth-status=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
      window.dispatchEvent(new Event(AUTH_STATUS_EVENT));
      
      // Redirect to login and force refresh to clear cache
      router.push('/login');
      router.refresh(); // Force refresh to clear Next.js cache
      
      toast.success('Logged out successfully');
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Logout failed');
    }
  };

  // Show login button if not authenticated
  if (!isAuthenticated) {
    return (
      <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container flex h-16 items-center justify-between px-4">
          <div className="flex items-center gap-6">
            <Link href="/" className="flex items-center space-x-2">
              <span className="font-bold inline-block">Coat-Rent</span>
            </Link>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" asChild>
              <Link href="/login">
                <User className="mr-2 h-4 w-4" />
                Sign In
              </Link>
            </Button>
          </div>
        </div>
      </header>
    );
  }

  // Show full navigation and logout if authenticated
  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-16 items-center justify-between px-4">
        <div className="flex items-center gap-6">
          <Link href="/" className="flex items-center space-x-2">
            <span className="font-bold inline-block">Coat-Rent</span>
          </Link>
          <MainNav />
        </div>
        <div className="flex items-center gap-2">
          <NotificationSystem enabled={true} checkInterval={5} />
          <span className="text-sm text-muted-foreground">Welcome, Admin</span>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleLogout}
            className="ml-2"
          >
            <LogOut className="mr-2 h-4 w-4" />
            Logout
          </Button>
        </div>
      </div>
    </header>
  );
}
